import {SpotlightSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import React, {ComponentType, useEffect} from 'react';
import {useIsAuthenticated} from '../../../features/Authentication/hooks/useIsAuthenticated';
import {TilesByPage} from '../../../features/ProductStream/TilesByPage';
import {ProductStreamTokenPagination} from '../../../features/ProductStreamPagination/ProductStreamTokenPagination';
import {useTokenBasedStreamPages} from '../../../features/TokenBasedStreamPages/useTokenBasedStreamPages';
import {
  useGetBrowseNextSections,
  useGetBrowseSections,
} from '../utils/useGetBrowseSections';
import {StreamComponentList} from './StreamComponentList';
import {SpotlightSkeleton, StreamSkeletons} from './StreamSkeletons';

interface StreamInterface {
  SliderComponent: ComponentType<SpotlightSection>;
  onAddToBasketCtaClick?: ({
    productId,
    filteredSizeId,
    couponPromotionId,
  }: {
    productId: number;
    filteredSizeId?: number;
    couponPromotionId?: string;
  }) => void;
}

export const Stream: React.FC<StreamInterface> = ({
  SliderComponent,
  onAddToBasketCtaClick,
}) => {
  const {isAuthenticated} = useIsAuthenticated();

  const {pages, isNextPageReady, mountNextPage} = useTokenBasedStreamPages({
    // Clear all cached pages on authentication state changes to prevent stale data issues.
    // Without clearing, pages would continue using old tokens, causing duplicate content when fetching subsequent pages with invalid continuation tokens.
    // Resetting ensures fresh token usage across all page requests after login/logout.
    uniqueKey: `browsePage-${isAuthenticated}`,
    request: useGetBrowseSections,
    nextRequest: useGetBrowseNextSections,
  });

  useEffect(() => {
    if (pages.length > 1) {
      return;
    }

    // When users navigate away from the browse page and then login/logout, the page state is reset.
    // Upon returning to the browse page, users would land to the footer.
    // This scroll ensures users start at the top of the page for a better experience.
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, [pages]);

  return (
    <>
      {pages.map((page, i) => (
        <TilesByPage
          key={i}
          page={page}
          loadingComponent={
            page.page === 1 ? SpotlightSkeleton : StreamSkeletons
          }
          gridColumns={undefined}
          StreamTileList={StreamComponentList}
          SliderComponent={SliderComponent}
          onAddToBasketCtaClick={onAddToBasketCtaClick}
        />
      ))}
      {isNextPageReady && (
        <ProductStreamTokenPagination
          mountNextPage={mountNextPage}
          rootMargin="0px"
        />
      )}
    </>
  );
};
